import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    reactive,
    ref,
    watch
} from 'vue';
import type { Scope } from '../../../runtime/services/StorageService';
import StorageService from '../../../runtime/services/StorageService';
import {
    buildEditedImageFilename,
    buildImageEditorFilename,
    getEditorExportFormatInfo,
    imageEditorWebMaxDimension,
    resolveImageEditorExportFormat,
    resolveUploadExportFormat,
    type ImageEditorCropMode,
    type ImageEditorDimensions,
    type ImageEditorExportFormat
} from './imageEditor';
import {
    defaultCropperAspectRatios,
    type AspectRatioOption,
    type CropperHistoryState,
    type CropSelection,
    type WebsiteLogoOption
} from './cropper';
import { createCropperCanvasActions } from './cropperCanvas';
import { createCropperHistoryActions } from './cropperHistory';
import { createCropperWatermarkActions } from './cropperWatermark';

export interface CropperEditorProps
{
    enabled: boolean;
    selected?: FileResource;
    sourceFile?: File | null;
    scope: Scope;
    folder?: number;
}

export type CropperEditorEmit = {
    (e: 'error'): void;
    (e: 'local-saved', value: File): void;
    (e: 'saved', value: FileResource): void;
};

function createCropperEditorState(props: CropperEditorProps, translate: (key: string) => string)
{
    const cropperStage = ref<any | null>(null);
    const cropperRightSidebar = ref<any | null>(null);
    const image = ref<HTMLImageElement | null>(null);
    const watermarkLogo = ref<HTMLImageElement | null>(null);
    const websiteLogo = ref<HTMLImageElement | null>(null);
    const canvas = computed(() => cropperStage.value?.canvas ?? null);
    const previewCanvas = computed(() => cropperRightSidebar.value?.previewCanvas ?? null);
    const loading = ref(false);
    const saving = ref(false);
    const websiteLogoLoading = ref(false);
    const cropMode = ref<ImageEditorCropMode>('none');
    const aspectRatio = ref('1.777');
    const fixedSize = reactive<ImageEditorDimensions>({ width: 800, height: 600 });
    const circularSize = ref(256);
    const templateSize = ref<ImageEditorDimensions | null>(null);
    const rotation = ref(0);
    const flipHorizontal = ref(false);
    const flipVertical = ref(false);
    const scale = ref(100);
    const brightness = ref(100);
    const contrast = ref(100);
    const saturation = ref(100);
    const grayscale = ref(0);
    const watermarkEnabled = ref(false);
    const watermarkType = ref<'text' | 'logo'>('text');
    const watermarkText = ref('© Edito');
    const watermarkX = ref(85);
    const watermarkY = ref(90);
    const watermarkOpacity = ref(70);
    const watermarkSize = ref(32);
    const exportFormat = ref<ImageEditorExportFormat>('original');
    const outputFileName = ref('');
    const quality = ref(90);
    const outputResizeEnabled = ref(false);
    const outputSize = reactive<ImageEditorDimensions>({ width: 0, height: 0 });
    const optimizeDimensions = ref(false);
    const maxDimension = ref(imageEditorWebMaxDimension);
    const customRatioWidth = ref(1200);
    const customRatioHeight = ref(630);
    const historyIndex = ref(-1);
    const history = ref<CropperHistoryState[]>([]);
    const customAspectRatios = ref<AspectRatioOption[]>([]);
    const websiteLogoOptions = ref<WebsiteLogoOption[]>([]);
    const selectedWebsiteLogoId = ref<number | null>(null);
    const sourceObjectUrl = ref<string | null>(null);
    const websiteLogoObjectUrl = ref<string | null>(null);
    const originalWidth = ref(0);
    const originalHeight = ref(0);
    const canvasWidth = ref(0);
    const canvasHeight = ref(0);
    const viewportZoom = ref(100);
    const cropSelection = reactive<CropSelection>({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        active: false
    });
    const cropSelectionStyle = computed(() =>
    {
        const borderRadius = cropMode.value === 'circular' ? '50%' : '0';
        const zoom = viewportZoom.value / 100;

        return {
            left: `${cropSelection.x * zoom}px`,
            top: `${cropSelection.y * zoom}px`,
            width: `${cropSelection.width * zoom}px`,
            height: `${cropSelection.height * zoom}px`,
            borderRadius
        };
    });
    const canvasDisplayStyle = computed(() =>
    {
        const zoom = viewportZoom.value / 100;

        return {
            width: `${canvasWidth.value * zoom}px`,
            height: `${canvasHeight.value * zoom}px`
        };
    });
    const filterString = computed(() => `brightness(${brightness.value}%) contrast(${contrast.value}%) saturate(${saturation.value}%) grayscale(${grayscale.value}%)`);
    const canSaveCustomAspectRatio = computed(() => customRatioWidth.value > 0 && customRatioHeight.value > 0);
    const editorFileName = computed(() => props.sourceFile?.name ?? props.selected?.name ?? '');
    const isLocalFileEditor = computed(() => !!props.sourceFile);
    const aspectRatioOptions = computed(() => [...defaultCropperAspectRatios, ...customAspectRatios.value]);
    const originalContentType = computed(() => props.sourceFile?.type ?? props.selected?.contentType ?? '');
    const resolvedExportFormat = computed(() => resolveImageEditorExportFormat(exportFormat.value, editorFileName.value, originalContentType.value));
    const exportQualityDisabled = computed(() => resolvedExportFormat.value === 'png');
    const exportFormatOptions = computed(() => [
        {
            value: 'original' as ImageEditorExportFormat,
            text: `${translate('[[[Oryginalny format]]]')} (${getEditorExportFormatInfo('original', 1, editorFileName.value, originalContentType.value).extension.toUpperCase()})`
        },
        {
            value: 'webp' as ImageEditorExportFormat,
            text: 'WebP'
        }
    ]);

    return {
        cropperStage,
        cropperRightSidebar,
        image,
        watermarkLogo,
        websiteLogo,
        canvas,
        previewCanvas,
        loading,
        saving,
        websiteLogoLoading,
        cropMode,
        aspectRatio,
        fixedSize,
        circularSize,
        templateSize,
        rotation,
        flipHorizontal,
        flipVertical,
        scale,
        brightness,
        contrast,
        saturation,
        grayscale,
        watermarkEnabled,
        watermarkType,
        watermarkText,
        watermarkX,
        watermarkY,
        watermarkOpacity,
        watermarkSize,
        exportFormat,
        outputFileName,
        quality,
        outputResizeEnabled,
        outputSize,
        optimizeDimensions,
        maxDimension,
        customRatioWidth,
        customRatioHeight,
        historyIndex,
        history,
        customAspectRatios,
        websiteLogoOptions,
        selectedWebsiteLogoId,
        sourceObjectUrl,
        websiteLogoObjectUrl,
        originalWidth,
        originalHeight,
        canvasWidth,
        canvasHeight,
        viewportZoom,
        cropSelection,
        cropSelectionStyle,
        canvasDisplayStyle,
        filterString,
        canSaveCustomAspectRatio,
        editorFileName,
        isLocalFileEditor,
        aspectRatioOptions,
        originalContentType,
        resolvedExportFormat,
        exportFormatOptions,
        exportQualityDisabled
    };
}

function createCropperEditorViewModel(state: ReturnType<typeof createCropperEditorState>)
{
    return {
        cropperStage: state.cropperStage,
        cropperRightSidebar: state.cropperRightSidebar,
        loading: state.loading,
        saving: state.saving,
        websiteLogoLoading: state.websiteLogoLoading,
        cropMode: state.cropMode,
        aspectRatio: state.aspectRatio,
        fixedSize: state.fixedSize,
        circularSize: state.circularSize,
        rotation: state.rotation,
        flipHorizontal: state.flipHorizontal,
        flipVertical: state.flipVertical,
        scale: state.scale,
        brightness: state.brightness,
        contrast: state.contrast,
        saturation: state.saturation,
        grayscale: state.grayscale,
        watermarkEnabled: state.watermarkEnabled,
        watermarkType: state.watermarkType,
        watermarkText: state.watermarkText,
        watermarkX: state.watermarkX,
        watermarkY: state.watermarkY,
        watermarkOpacity: state.watermarkOpacity,
        watermarkSize: state.watermarkSize,
        exportFormat: state.exportFormat,
        outputFileName: state.outputFileName,
        quality: state.quality,
        outputResizeEnabled: state.outputResizeEnabled,
        outputSize: state.outputSize,
        optimizeDimensions: state.optimizeDimensions,
        customRatioWidth: state.customRatioWidth,
        customRatioHeight: state.customRatioHeight,
        historyIndex: state.historyIndex,
        history: state.history,
        websiteLogoOptions: state.websiteLogoOptions,
        selectedWebsiteLogoId: state.selectedWebsiteLogoId,
        originalWidth: state.originalWidth,
        originalHeight: state.originalHeight,
        viewportZoom: state.viewportZoom,
        cropSelection: state.cropSelection,
        cropSelectionStyle: state.cropSelectionStyle,
        canvasDisplayStyle: state.canvasDisplayStyle,
        canSaveCustomAspectRatio: state.canSaveCustomAspectRatio,
        editorFileName: state.editorFileName,
        isLocalFileEditor: state.isLocalFileEditor,
        aspectRatioOptions: state.aspectRatioOptions,
        exportFormatOptions: state.exportFormatOptions,
        exportQualityDisabled: state.exportQualityDisabled
    };
}

export function createCropperEditor(props: CropperEditorProps, emit: CropperEditorEmit)
{
    const { $alert } = useAlerts();
    const { $t } = useLocalization();
    const { $log } = useLogging();

    const state = createCropperEditorState(props, $t);

    function getOutputFileName()
    {
        return buildImageEditorFilename(state.outputFileName.value, state.exportFormat.value, state.editorFileName.value, state.originalContentType.value);
    }

    function setDefaultOutputFileName()
    {
        state.outputFileName.value = buildEditedImageFilename(state.editorFileName.value, state.exportFormat.value, 'edited');
    }

    const canvasActions = createCropperCanvasActions({
        canvas: state.canvas,
        previewCanvas: state.previewCanvas,
        image: state.image,
        watermarkLogo: state.watermarkLogo,
        canvasWidth: state.canvasWidth,
        canvasHeight: state.canvasHeight,
        viewportZoom: state.viewportZoom,
        cropSelection: state.cropSelection,
        cropMode: state.cropMode,
        aspectRatio: state.aspectRatio,
        fixedSize: state.fixedSize,
        circularSize: state.circularSize,
        templateSize: state.templateSize,
        rotation: state.rotation,
        flipHorizontal: state.flipHorizontal,
        flipVertical: state.flipVertical,
        scale: state.scale,
        filterString: state.filterString,
        watermarkEnabled: state.watermarkEnabled,
        watermarkType: state.watermarkType,
        watermarkText: state.watermarkText,
        watermarkX: state.watermarkX,
        watermarkY: state.watermarkY,
        watermarkOpacity: state.watermarkOpacity,
        watermarkSize: state.watermarkSize,
        exportFormat: state.resolvedExportFormat,
        outputResizeEnabled: state.outputResizeEnabled,
        outputSize: state.outputSize,
        optimizeDimensions: state.optimizeDimensions,
        maxDimension: state.maxDimension,
        saveHistory: label => historyActions.saveHistory(label),
        translate: $t
    });

    const {
        resetSelection,
        redrawCanvas,
        updatePreview
    } = canvasActions;

    const historyActions = createCropperHistoryActions({
        history: state.history,
        historyIndex: state.historyIndex,
        cropSelection: state.cropSelection,
        cropMode: state.cropMode,
        aspectRatio: state.aspectRatio,
        fixedSize: state.fixedSize,
        circularSize: state.circularSize,
        templateSize: state.templateSize,
        rotation: state.rotation,
        flipHorizontal: state.flipHorizontal,
        flipVertical: state.flipVertical,
        scale: state.scale,
        brightness: state.brightness,
        contrast: state.contrast,
        saturation: state.saturation,
        grayscale: state.grayscale,
        watermarkEnabled: state.watermarkEnabled,
        watermarkText: state.watermarkText,
        watermarkType: state.watermarkType,
        watermarkX: state.watermarkX,
        watermarkY: state.watermarkY,
        watermarkOpacity: state.watermarkOpacity,
        watermarkSize: state.watermarkSize,
        exportFormat: state.exportFormat,
        quality: state.quality,
        outputResizeEnabled: state.outputResizeEnabled,
        outputSize: state.outputSize,
        optimizeDimensions: state.optimizeDimensions,
        redrawCanvas
    });

    const {
        saveHistory
    } = historyActions;

    function applyCustomAspectRatio(save: boolean = false)
    {
        if (!state.canSaveCustomAspectRatio.value)
            return;

        state.aspectRatio.value = (state.customRatioWidth.value / state.customRatioHeight.value).toFixed(4);
        resetSelection();

        if (save)
        {
            const text = `${state.customRatioWidth.value}:${state.customRatioHeight.value}`;
            const option = {
                value: state.aspectRatio.value,
                text
            };

            state.customAspectRatios.value = [option, ...state.customAspectRatios.value.filter(item => item.text !== text)].slice(0, 12);
            localStorage.setItem('filemanager-image-editor-aspect-ratios', JSON.stringify(state.customAspectRatios.value));
        }

        saveHistory($t('[[[Zmiana proporcji kadrowania]]]'));
    }

    function resetEditor()
    {
        state.cropMode.value = 'none';
        state.rotation.value = 0;
        state.flipHorizontal.value = false;
        state.flipVertical.value = false;
        state.scale.value = 100;
        state.brightness.value = 100;
        state.contrast.value = 100;
        state.saturation.value = 100;
        state.grayscale.value = 0;
        state.watermarkEnabled.value = false;
        state.watermarkType.value = props.sourceFile ? 'logo' : 'text';
        state.watermarkText.value = '© Edito';
        state.watermarkLogo.value = null;
        state.watermarkSize.value = 32;
        state.exportFormat.value = 'original';
        setDefaultOutputFileName();
        state.quality.value = 90;
        state.outputResizeEnabled.value = false;
        state.outputSize.width = state.originalWidth.value;
        state.outputSize.height = state.originalHeight.value;
        state.optimizeDimensions.value = false;
        state.cropSelection.active = false;
        redrawCanvas();
        saveHistory($t('[[[Reset edytora]]]'));
    }

    const watermarkActions = createCropperWatermarkActions({
        websiteLogoLoading: state.websiteLogoLoading,
        websiteLogo: state.websiteLogo,
        websiteLogoOptions: state.websiteLogoOptions,
        selectedWebsiteLogoId: state.selectedWebsiteLogoId,
        websiteLogoObjectUrl: state.websiteLogoObjectUrl,
        watermarkLogo: state.watermarkLogo,
        watermarkEnabled: state.watermarkEnabled,
        watermarkType: state.watermarkType,
        watermarkText: state.watermarkText,
        watermarkX: state.watermarkX,
        watermarkY: state.watermarkY,
        saveHistory,
        updatePreview,
        translate: $t
    });

    async function canvasToFile(fileName?: string)
    {
        if (!state.previewCanvas.value)
            throw new Error('Canvas is not ready.');

        const format = props.sourceFile ? resolveUploadExportFormat(props.sourceFile) : state.exportFormat.value;
        const info = getEditorExportFormatInfo(format, props.sourceFile ? 1 : state.quality.value / 100, state.editorFileName.value, state.originalContentType.value);
        const resolvedFileName = fileName || buildEditedImageFilename(state.editorFileName.value, format, 'edited');

        return await new Promise<File>((resolve, reject) =>
        {
            state.previewCanvas.value.toBlob((blob: Blob | null) =>
            {
                if (!blob)
                {
                    reject(new Error('Export failed.'));

                    return;
                }

                resolve(new File([blob], resolvedFileName, { type: blob.type || info.mimeType }));
            }, info.mimeType, info.quality);
        });
    }

    async function saveEditedImage()
    {
        try
        {
            state.saving.value = true;
            updatePreview();

            const file = await canvasToFile(state.isLocalFileEditor.value ? undefined : getOutputFileName());

            if (state.isLocalFileEditor.value)
            {
                emit('local-saved', file);

                return;
            }

            const result = await StorageService.uploadEditedImage(file, props.scope, props.folder);

            $alert.success($t('[[[Obraz został zapisany jako nowy plik.]]]'));
            emit('saved', result);
        }
        catch (ex)
        {
            handleException($log, ex, {
                400: (error: any) => $alert.warning($t(error.message)),
                0: () => $alert.error($t('[[[Nie udało się zapisać obrazu.]]]'))
            });
        }
        finally
        {
            state.saving.value = false;
        }
    }

    function loadImage()
    {
        state.loading.value = true;

        const element = new Image();
        const source = props.sourceFile ? URL.createObjectURL(props.sourceFile) : baseurl(props.selected.url);

        if (state.sourceObjectUrl.value)
        {
            URL.revokeObjectURL(state.sourceObjectUrl.value);
        }

        state.sourceObjectUrl.value = props.sourceFile ? source : null;

        element.addEventListener('load', () =>
        {
            state.image.value = element;
            state.originalWidth.value = element.width;
            state.originalHeight.value = element.height;
            state.outputSize.width = element.width;
            state.outputSize.height = element.height;
            state.loading.value = false;
            setDefaultOutputFileName();

            nextTick(() =>
            {
                redrawCanvas();
                saveHistory($t('[[[Wczytanie obrazu]]]'));
            });
        }, { once: true });
        element.addEventListener('error', () =>
        {
            if (state.sourceObjectUrl.value)
            {
                URL.revokeObjectURL(state.sourceObjectUrl.value);
                state.sourceObjectUrl.value = null;
            }

            state.loading.value = false;
            $alert.error($t('[[[Wystąpił błąd podczas ładowania obrazu. Spróbuj ponownie.]]]'));
            emit('error');
        }, { once: true });
        element.src = source;
    }

    onMounted(() =>
    {
        if (props.sourceFile)
        {
            state.watermarkType.value = 'logo';
        }

        const storedAspectRatios = localStorage.getItem('filemanager-image-editor-aspect-ratios');

        if (storedAspectRatios)
        {
            state.customAspectRatios.value = JSON.parse(storedAspectRatios);
        }

        loadImage();
        watermarkActions.loadWebsiteLogo();
    });

    onUnmounted(() =>
    {
        if (state.sourceObjectUrl.value)
        {
            URL.revokeObjectURL(state.sourceObjectUrl.value);
        }

        if (state.websiteLogoObjectUrl.value)
        {
            URL.revokeObjectURL(state.websiteLogoObjectUrl.value);
        }
    });

    watch([
        state.scale,
        state.brightness,
        state.contrast,
        state.saturation,
        state.grayscale,
        state.watermarkEnabled,
        state.watermarkText,
        state.watermarkX,
        state.watermarkY,
        state.watermarkOpacity,
        state.watermarkSize,
        state.exportFormat,
        state.quality,
        state.outputResizeEnabled,
        () => state.outputSize.width,
        () => state.outputSize.height,
        state.optimizeDimensions
    ], () =>
    {
        redrawCanvas();
    });

    watch(state.exportFormat, () =>
    {
        if (!state.isLocalFileEditor.value)
        {
            state.outputFileName.value = getOutputFileName();
        }
    });

    return {
        ...createCropperEditorViewModel(state),
        ...canvasActions,
        ...historyActions,
        ...watermarkActions,
        applyCustomAspectRatio,
        saveEditedImage,
        resetEditor
    };
}
